import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from './config';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ForgotEmail from './pages/ForgotEmail';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CreateLink from './pages/CreateLink';
import Analytics from './pages/Analytics';
import Upgrade from './pages/Upgrade';
import AdminDashboard from './pages/AdminDashboard';
import BrandSettings from './pages/BrandSettings';
import Home from './pages/Home';
import About from './pages/About';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Contact from './pages/Contact';
import Features from './pages/Features';
import NotFound from './pages/NotFound';
import NavBar from './components/NavBar';
import ErrorBoundary from './components/ErrorBoundary';
import logoImg from './assets/images/logo.png';

const FOOTER_STYLES = `
  .rdrto-footer {
    font-family: 'Inter', system-ui, sans-serif;
    background: #060a14;
    border-top: 1px solid rgba(99, 102, 241, 0.15);
    padding: 64px 24px 32px;
    color: #475569;
  }
  .rdrto-footer-inner {
    max-width: 1200px;
    margin: 0 auto;
  }
  .rdrto-footer-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: 40px;
    margin-bottom: 48px;
  }
  @media (max-width: 768px) {
    .rdrto-footer-grid { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 480px) {
    .rdrto-footer-grid { grid-template-columns: 1fr; }
  }
  .footer-brand-name {
    font-size: 1.4rem;
    font-weight: 900;
    color: #f1f5f9;
    letter-spacing: -0.02em;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .footer-brand-img {
    height: 28px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    border-radius: 6px;
    filter: drop-shadow(0 0 14px rgba(99, 102, 241, 0.4));
  }
  .footer-tagline {
    font-size: 13px;
    color: #334155;
    margin-bottom: 16px;
    font-style: italic;
  }
  .footer-brand-desc {
    font-size: 13px;
    line-height: 1.75;
    color: #334155;
    max-width: 280px;
  }
  .footer-col-title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #64748b;
    margin-bottom: 20px;
  }
  .footer-links {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .footer-links a {
    color: #475569;
    text-decoration: none;
    font-size: 13px;
    transition: color 0.2s;
  }
  .footer-links a:hover { color: #a5b4fc; }
  .footer-bottom {
    border-top: 1px solid rgba(99, 102, 241, 0.1);
    padding-top: 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }
  .footer-copy {
    font-size: 12px;
    color: #334155;
  }
  .footer-legal {
    display: flex;
    gap: 20px;
  }
  .footer-legal a {
    font-size: 12px;
    color: #334155;
    text-decoration: none;
    transition: color 0.2s;
  }
  .footer-legal a:hover { color: #a5b4fc; }
`;

const Footer = () => (
  <footer className="rdrto-footer">
    <style>{FOOTER_STYLES}</style>
    <div className="rdrto-footer-inner">
      <div className="rdrto-footer-grid">
        <div>
          <div className="footer-brand-name">
            <img src={new URL('./assets/images/logo.png', import.meta.url).href} alt="RDRTO" className="footer-brand-img" />
            RDRTO
          </div>
          <p className="footer-tagline">ReDiRect TO — the smarter way to share any link.</p>
          <p className="footer-brand-desc">
            A fast, reliable, and free URL shortening platform with real-time analytics,
            custom aliases, and enterprise-grade link management tools.
          </p>
        </div>

        <div>
          <div className="footer-col-title">Product</div>
          <ul className="footer-links">
            <li><a href="/features">Features</a></li>
            <li><a href="/upgrade">Pricing</a></li>
            <li><a href="/dashboard">Dashboard</a></li>
            <li><a href="/signup">Get Started</a></li>
          </ul>
        </div>

        <div>
          <div className="footer-col-title">Company</div>
          <ul className="footer-links">
            <li><a href="/about">About RDRTO</a></li>
            <li><a href="/contact">Contact</a></li>
            <li><a href="/contact">Report Abuse</a></li>
          </ul>
        </div>

        <div>
          <div className="footer-col-title">Legal</div>
          <ul className="footer-links">
            <li><a href="/privacy">Privacy Policy</a></li>
            <li><a href="/terms">Terms of Service</a></li>
          </ul>
        </div>
      </div>

      <div className="footer-bottom">
        <p className="footer-copy">&copy; 2024 RDRTO. All rights reserved.</p>
        <div className="footer-legal">
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/contact">Contact</a>
        </div>
      </div>
    </div>
  </footer>
);



function AppContent() {
  const { user, loading, logout } = useAuth();
  const isAuthenticated = !!user;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <p style={{ color: 'white', fontSize: '18px' }}>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <NavBar />
        <Routes>
          {/* Public pages */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/features" element={<Features />} />

          {/* Auth pages */}
          <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />} />
          <Route path="/signup" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/forgot-email" element={<ForgotEmail />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Protected app pages */}
          <Route path="/dashboard" element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />} />
          <Route path="/create" element={isAuthenticated ? <CreateLink /> : <Navigate to="/login" replace />} />
          <Route path="/analytics/:id" element={isAuthenticated ? <Analytics /> : <Navigate to="/login" replace />} />
          <Route path="/upgrade" element={<Upgrade />} />
          <Route path="/admin" element={isAuthenticated ? <AdminDashboard /> : <Navigate to="/login" replace />} />
          <Route path="/brand-settings" element={isAuthenticated ? <BrandSettings /> : <Navigate to="/login" replace />} />

          {/* Catch-all route for unknown paths */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
